import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Collapse,
  Breadcrumbs,
  Link as MuiLink,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  InputAdornment,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import {
  QuickAddCustomer,
  QuickAddVendor,
  QuickAddInventoryItem,
  QuickAddOpportunity,
  QuickAddLeaveRequest
} from '../shared/QuickAddModals';
import {
  Menu as MenuIcon,
  Groups,
  Apartment,
  Dashboard,
  AccountTree,
  Receipt,
  ReceiptLong,
  RequestQuote,
  ShoppingCart,
  Inventory as InventoryIcon,
  Inventory2,
  Category,
  Assessment,
  Settings as SettingsIcon,
  Logout,
  AccountCircle,
  ChevronLeft,
  ChevronRight,
  People,
  Business,
  Group,
  PersonSearch,
  Security,
  LocalAtm,
  Storefront,
  InventoryOutlined,
  BatchPrediction,
  Route,
  TaskAlt,
  Schedule,
  Assignment,
  Policy,
  Notifications,
  PointOfSale,
  Stars,
  ExpandMore,
  ExpandLess,
  CreditCard,
  TrendingUp,
  Warehouse,
  LocalShipping,
  SwapHoriz,
  Tune,
  Verified,
  Description,
  MoneyOff,
  AccountBalance,
  SupervisorAccount,
  Work,
  Handshake,
  Api,
  QrCode,
  NavigateNext,
  NavigateBefore,
  ContactMail,
  MenuBook as MenuBookIcon,
  OpenInNew as OpenInNewIcon,
  History as HistoryIcon,
  AssignmentTurnedIn,
  AutoAwesome as AutoAwesomeIcon,
  CloudUpload as CloudUploadIcon,
  PrecisionManufacturing,
  Factory,
  Engineering,
  Gavel,
  CurrencyExchange,
  AccountBalanceWallet,
  SupportAgent,
  Article as ArticleIcon,
  ContactPhone,
  Speed
} from '@mui/icons-material';
import { useThemeMode } from '../../context/ThemeContext';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { companiesAPI } from '../../services/api';
import LanguageSelector from '../settings/LanguageSelector';
import CompanySwitcher from '../shared/CompanySwitcher';
import ErrorBoundary from '../common/ErrorBoundary';
import AlertNotificationCenter from '../shared/AlertNotificationCenter';
import LoadingSpinner from '../common/LoadingSpinner';

// Paths that are restricted to UK/GB companies only
const UK_ONLY_PATHS = new Set(['/reports/uk-statutory', '/accounting/hmrc-mtd']);

// Groups that require Professional/Enterprise plan (not available on Solo)
const PLAN_RESTRICTED_GROUPS = new Set(['trade', 'manufacturing']);

const isUKCompany = (country) => {
  if (!country) return false;
  const c = country.toUpperCase().trim();
  return c === 'GB' || c === 'UK' || c === 'UNITED KINGDOM';
};

const drawerWidth = 260;

// Sidebar structure: logically ordered and complete coverage
const getMenuItems = (t, userRole) => [
  // ========== CORE ==========
  {
    text: t('nav.dashboard'),
    icon: <Dashboard />,
    path: '/dashboard',
    group: 'core',
    order: 1
  },

  // ========== SALES ==========
  {
    text: t('nav.sales', 'Sales'),
    icon: <Storefront />,
    group: 'sales',
    order: 2,
    permission: 'sales.invoice.view', // General sales permission
    children: [
      { text: t('sales.quotations'), path: '/quotations', icon: <Description />, permission: 'sales.quotation.view' },
      { text: t('sales.orders'), path: '/sales-orders', icon: <AssignmentTurnedIn />, permission: 'sales.order.view' },
      { text: t('sales.invoices'), path: '/sales-invoices', icon: <RequestQuote />, permission: 'sales.invoice.view' },
      { text: t('sales.returns'), path: '/sales-returns', icon: <SwapHoriz />, permission: 'sales.return.view' },
      { text: t('sales.receipts', 'Receipts'), path: '/sales/receipts', icon: <LocalAtm />, permission: 'sales.receipt.view' },
      { text: t('sales.installmentSales', 'Installment Sales'), path: '/sales/installment-sales', icon: <CreditCard />, permission: 'sales.installment.view' },
      { divider: true },
      { text: t('sales.customerRates', 'Customer Pricing'), path: '/sales/customer-rates', icon: <LocalAtm />, permission: 'sales.customer_pricing.view' },
    ]
  },

  // ========== CRM ==========
  {
    text: t('nav.crm', 'CRM'),
    icon: <Business />,
    group: 'crm',
    order: 2.5,
    permission: 'crm.customer.view',
    children: [
      { text: t('crm.customers'), path: '/crm/customers', icon: <Business />, permission: 'crm.customer.view' },
      { text: t('crm.leads'), path: '/crm/leads', icon: <PersonSearch />, permission: 'crm.opportunity.view' },
      { text: t('crm.opportunities'), path: '/crm/opportunities', icon: <TrendingUp />, permission: 'crm.opportunity.view' },
      { text: t('crm.tickets'), path: '/crm/tickets', icon: <ContactMail />, permission: 'crm.ticket.view' },
      { text: t('crm.activities'), path: '/crm/activities', icon: <HistoryIcon />, permission: 'crm.activity.view' },
      { text: t('crm.loyalty'), path: '/loyalty-management', icon: <Stars />, permission: 'loyalty.program.view' },
      { text: t('crm.commissionAgents'), path: '/commission-agents', icon: <PersonSearch />, permission: 'crm.commission_agent.view' }
    ]
  },

  // ========== PROCUREMENT CYCLE ==========
  {
    text: t('nav.procurement'),
    icon: <LocalShipping />,
    group: 'procurement',
    order: 3,
    permission: 'procurement.invoice.view',
    children: [
      { text: t('procurement.requisitions'), path: '/purchase-requisitions', icon: <AssignmentTurnedIn />, permission: 'procurement.requisition.view' },
      { text: t('procurement.orders'), path: '/purchase-orders', icon: <ShoppingCart />, permission: 'procurement.order.view' },
      { text: t('procurement.invoices'), path: '/purchase-invoices', icon: <RequestQuote />, permission: 'procurement.invoice.view' },
      { text: t('procurement.returns'), path: '/purchase-returns', icon: <SwapHoriz />, permission: 'procurement.return.view' },
      { text: t('procurement.payments', 'Payments'), path: '/procurement/payments', icon: <CreditCard />, permission: 'procurement.payment.view' },
      { text: t('procurement.goodsReceiptNotes'), path: '/inventory/grns', icon: <Verified />, permission: 'procurement.grn.view' },
      { divider: true },
      { text: t('procurement.aiDocuments', 'AI Document Inbox'), path: '/ai-documents', icon: <AutoAwesomeIcon />, permission: 'ai.document.view' },
      { divider: true },
      { text: t('procurement.vendors'), path: '/crm/vendors', icon: <Group />, permission: 'crm.vendor.view' },
      { text: t('procurement.vendorGroups', 'Vendor Groups'), path: '/crm/vendor-groups', icon: <Groups />, permission: 'crm.vendor.view' },
      { divider: true },
      { text: t('procurement.vendorQuotations', 'Vendor Quotations'), path: '/vendor-quotations', icon: <RequestQuote />, permission: 'procurement.vendor_quote.view' },
    ]
  },

  // ========== INVENTORY & WAREHOUSE ==========
  {
    text: t('nav.inventoryWarehouse'),
    icon: <Inventory2 />,
    group: 'inventory',
    order: 4,
    permission: 'inventory.item.view',
    children: [
      { text: t('inventory.stock'), path: '/inventory/stock-list', icon: <Inventory2 />, permission: 'inventory.item.view' },
      { text: t('inventory.transfers'), path: '/inventory/stock-transfers', icon: <SwapHoriz />, permission: 'inventory.stock_transfer.view' },
      { text: t('inventory.adjustments'), path: '/inventory/stock-adjustments', icon: <Tune />, permission: 'inventory.stock_adjustment.view' },
      { text: t('inventory.picking'), path: '/picking', icon: <QrCode />, permission: 'inventory.item.view' },
      { text: t('procurement.shippingDeliveryNotes'), path: '/inventory/shipments', icon: <LocalShipping />, permission: 'inventory.shipment.view' },
      { divider: true },
      { text: t('inventory.warehouses'), path: '/inventory/warehouses', icon: <Warehouse />, permission: 'inventory.warehouse.view' },
      { text: t('inventory.bins'), path: '/inventory/warehouse-bins', icon: <InventoryOutlined />, permission: 'inventory.bin.view' },
      { text: t('inventory.cycleCounts'), path: '/inventory/cycle-counting', icon: <AssignmentTurnedIn />, permission: 'inventory.cycle_count.view' },
      { text: t('inventory.traceability'), path: '/inventory/traceability', icon: <HistoryIcon />, permission: 'inventory.item.view' },
      { text: t('inventory.batches'), path: '/inventory/batches', icon: <BatchPrediction />, permission: 'inventory.batch.view' },
      { divider: true },
      { text: t('inventory.items'), path: '/inventory/item-list', icon: <Category />, permission: 'inventory.item.view' },
      { text: t('inventory.categories'), path: '/item-categories', icon: <Category />, permission: 'inventory.category.view' },
      { text: t('inventory.brands'), path: '/inventory/brands', icon: <Category />, permission: 'inventory.brand.view' },
      { text: t('inventory.units'), path: '/inventory/units', icon: <Tune />, permission: 'inventory.unit.view' },
      { text: t('inventory.landedCosts'), path: '/inventory/landed-costs', icon: <LocalAtm />, permission: 'inventory.landed_cost.view' },
    ]
  },

  // ========== MANUFACTURING ==========
  {
    text: t('nav.manufacturing'),
    icon: <Factory />,
    group: 'manufacturing',
    order: 5,
    permission: 'manufacturing.bom.view',
    children: [
      { text: t('manufacturing.boms'), path: '/manufacturing/boms', icon: <PrecisionManufacturing />, permission: 'manufacturing.bom.view' },
      { text: t('manufacturing.productionOrders'), path: '/manufacturing/production-orders', icon: <Engineering />, permission: 'manufacturing.production_order.view' },
      { text: t('manufacturing.workCenters'), path: '/manufacturing/work-centers', icon: <Factory />, permission: 'manufacturing.work_center.view' },
      { text: t('manufacturing.routings'), path: '/manufacturing/routings', icon: <Route />, permission: 'manufacturing.bom.view' },
      { divider: true },
      { text: t('manufacturing.qcInspections', 'QC Inspections'), path: '/manufacturing/qc-inspections', icon: <Verified />, permission: 'manufacturing.qc.view' },
    ]
  },

  // ========== ASSET MANAGEMENT (EAM) ==========
  {
    text: t('nav.eam', 'Asset Management'),
    icon: <Engineering />,
    group: 'eam',
    order: 5.5,
    permission: 'eam.acquisition.view',
    children: [
      { text: t('finance.assetManagement', 'Fixed Assets'), path: '/accounting/fixed-assets', icon: <AccountBalanceWallet />, permission: 'finance.journal_entry.view' },
      { text: t('finance.leaseAccounting', 'Lease Accounting'), path: '/accounting/leases', icon: <ReceiptLong />, permission: 'finance.lease.view' },
      { text: t('eam.maintenance', 'Maintenance Board'), path: '/eam/maintenance', icon: <AssignmentTurnedIn />, permission: 'eam.maintenance.view' },
      { text: t('eam.inspections', 'Inspections & Meters'), path: '/eam/inspection', icon: <Speed />, permission: 'eam.inspection.view' },
      { text: t('eam.warranties', 'Warranties & Claims'), path: '/eam/warranty', icon: <Gavel />, permission: 'eam.warranty.view' },
      { text: t('eam.acquisitionRequests', 'Acquisition Requests'), path: '/eam/acquisition-requests', icon: <Description />, permission: 'eam.acquisition.view' },
      { text: t('eam.capitalization', 'Capitalizations'), path: '/eam/capitalization', icon: <AccountBalanceWallet />, permission: 'eam.acquisition.view' },
      { text: t('eam.transfers', 'Transfers'), path: '/eam/transfers', icon: <SwapHoriz />, permission: 'eam.transfer.view' },
      { text: t('eam.disposals', 'Disposals'), path: '/eam/disposals', icon: <TrendingUp />, permission: 'eam.disposal.view' },
      { divider: true },
      { text: t('eam.categories', 'Asset Categories'), path: '/eam/categories', icon: <Category />, permission: 'eam.category.view' }
    ]
  },

  // ========== TRADE & LOGISTICS ==========
  {
    text: t('nav.trade'),
    icon: <CurrencyExchange />,
    group: 'trade',
    order: 6,
    permission: 'trade.shipment.view',
    children: [
      { text: t('trade.shipments'), path: '/trade/shipments', icon: <LocalShipping />, permission: 'trade.shipment.view' },
      { text: t('trade.freightBookings'), path: '/trade/freight-bookings', icon: <LocalShipping />, permission: 'trade.shipment.view' },
      { text: t('trade.containers'), path: '/trade/containers', icon: <LocalShipping />, permission: 'trade.shipment.view' },
      { text: t('trade.costingDashboard'), path: '/trade/costing-dashboard', icon: <LocalShipping />, permission: 'trade.shipment.view' },
      { divider: true },
      { text: t('trade.lcs'), path: '/trade/letters-of-credit', icon: <Description />, permission: 'trade.lc.view' },
      { text: t('trade.customs'), path: '/trade/customs-declarations', icon: <Gavel />, permission: 'trade.customs.view' },
      { divider: true },
      { text: t('trade.hsCodes'), path: '/trade/hs-codes', icon: <QrCode />, permission: 'trade.hs_code.view' },
      { text: t('trade.compliance'), path: '/trade/compliance', icon: <Verified />, permission: 'trade.compliance.view' },
    ]
  },

  // ========== FINANCE & ACCOUNTING ==========
  {
    text: t('nav.financeAccounting'),
    icon: <AccountBalance />,
    group: 'finance',
    order: 7,
    permission: 'finance.journal_entry.view',
    children: [
      // General Ledger
      { text: t('finance.chartOfAccounts'), path: '/chart-of-accounts', icon: <AccountTree />, permission: 'finance.journal_entry.view' },
      { text: t('finance.journalEntries'), path: '/journal-entries', icon: <Description />, permission: 'finance.journal_entry.view' },
      { text: t('finance.allPayments', 'Payments & Receipts'), path: '/payments', icon: <CreditCard />, permission: 'finance.payment.view' },
      { divider: true },
      { text: t('finance.recurringEntries'), path: '/recurring-journal-entries', icon: <TrendingUp />, permission: 'finance.recurring_entry.view' },
      { text: t('finance.leaseAccounting', 'Lease Accounting'), path: '/accounting/leases', icon: <ReceiptLong />, permission: 'finance.lease.view' },
      { divider: true },

      // Expenses & Banking
      { text: t('finance.expenses'), path: '/expenses', icon: <MoneyOff />, permission: 'finance.expense.view' },
      { text: t('finance.expenseCategories', 'Expense Categories'), path: '/expense-categories', icon: <Category />, permission: 'finance.expense.view' },
      { text: t('finance.reconciliation'), path: '/bank-reconciliation', icon: <AccountBalance />, permission: 'finance.journal_entry.view' },
      { text: t('finance.creditNotes'), path: '/credit-notes', icon: <TrendingUp />, permission: 'sales.credit_note.view' },
      { text: t('finance.debitNotes'), path: '/debit-notes', icon: <MoneyOff />, permission: 'procurement.debit_note.view' },
      { divider: true },

      // Equity
      { text: t('finance.shareholders'), path: '/accounting/shareholders', icon: <People />, permission: 'finance.journal_entry.view' },
      { divider: true },
      // Trade Finance
      { text: t('finance.lettersOfCredit'), path: '/trade/letters-of-credit', icon: <Description />, permission: 'trade.lc.view' },
      { text: t('finance.bankGuarantees'), path: '/bank-guarantees', icon: <AccountBalance />, permission: 'trade.lc.view' },
      { divider: true },
      { text: t('finance.pettyCash', 'Petty Cash'), path: '/accounting/petty-cash', icon: <LocalAtm />, permission: 'finance.petty_cash.view' },
    ]
  },

  // ========== BUDGETING & PLANNING ==========
  {
    text: t('nav.budgetingPlanning'),
    icon: <AccountBalanceWallet />,
    group: 'budgeting',
    order: 8,
    permission: 'finance.budget.view',
    children: [
      { text: t('budgeting.periods'), path: '/budgeting/periods', icon: <HistoryIcon />, permission: 'finance.budget.view' },
      { text: t('budgeting.budgets'), path: '/budgeting/budgets', icon: <AccountBalanceWallet />, permission: 'finance.budget.view' },
      { text: t('budgeting.allocations'), path: '/budgeting/allocations', icon: <SwapHoriz />, permission: 'finance.budget.view' },
      { text: t('budgeting.monitoring'), path: '/budgeting/monitoring', icon: <Assessment />, permission: 'finance.budget.view' }
    ]
  },

  // ========== HUMAN RESOURCES ==========
  {
    text: t('nav.humanResources'),
    icon: <People />,
    group: 'hr',
    order: 9,
    permission: 'hr.employee.view',
    children: [
      { text: t('hr.employeeDashboard', 'Employee Dashboard'), path: '/employee-dashboard', icon: <Dashboard />, permission: 'hr.employee.view' },
      { text: t('hr.employees'), path: '/employees', icon: <People />, permission: 'hr.employee.view' },
      { text: t('hr.attendance'), path: '/attendance', icon: <Groups />, permission: 'hr.attendance.view' },
      { text: t('hr.leave'), path: '/hr/leave', icon: <LocalAtm />, permission: 'hr.leave.view' },
      { text: t('hr.leaveManagement', 'Leave Management'), path: '/hr/leave-management', icon: <AssignmentTurnedIn />, permission: 'hr.leave.view' },
      { text: t('hr.payroll'), path: '/payroll', icon: <CreditCard />, permission: 'hr.payroll.manage' },
      { divider: true },
      { text: t('hr.recruitment'), path: '/hr/recruiting', icon: <Work />, permission: 'hr.recruiting.view' },
      { text: t('hr.performance'), path: '/hr/performance', icon: <Assessment />, permission: 'hr.performance.view' },
      { text: t('hr.performanceDashboard', 'Performance Dashboard'), path: '/hr/performance-dashboard', icon: <TrendingUp />, permission: 'hr.performance.view' },
      { text: t('hr.targets', 'Employee Targets'), path: '/hr/targets', icon: <Stars />, permission: 'hr.performance.view' },
      { text: t('hr.kpiSettings', 'KPI Settings'), path: '/hr/kpi-settings', icon: <Tune />, permission: 'hr.performance.configure' },
      { text: t('hr.training'), path: '/hr/training', icon: <MenuBookIcon />, permission: 'hr.training.view' },
      { divider: true },
      { text: t('hr.departments'), path: '/departments', icon: <Apartment />, permission: 'hr.department.view' },
      { divider: true },
      { text: t('hr.workShifts'), path: '/hr/shifts', icon: <Schedule />, permission: 'hr.attendance.view' },
      { divider: true },
      { text: t('hr.biometric', 'Biometric Devices'), path: '/hr/biometric', icon: <QrCode />, permission: 'hr.biometric.view' },
    ]
  },

  // ========== PROJECTS & WORK MANAGEMENT ==========
  {
    text: t('nav.projects'),
    icon: <AccountTree />,
    group: 'projects',
    order: 10,
    permission: 'crm.project.view',
    children: [
      { text: t('projects.projects'), path: '/projects', icon: <AccountTree />, permission: 'crm.project.view' },
      { text: t('projects.tasks', 'Task Manager'), path: '/tasks', icon: <TaskAlt />, permission: 'crm.task.view' },
      { text: t('projects.timesheets'), path: '/projects/timesheets', icon: <Schedule />, permission: 'crm.timesheet.view' }
    ]
  },

  // ========== RETAIL & POS ==========
  {
    text: t('nav.retailPos'),
    icon: <PointOfSale />,
    group: 'retail',
    order: 11,
    permission: 'pos.sale.view',
    children: [
      { text: t('retail.pos'), path: '/pos', icon: <PointOfSale />, permission: 'pos.sale.view' },
      { text: t('retail.cashDrawer'), path: '/pos/cash-drawer', icon: <LocalAtm />, permission: 'pos.sale.view' },
      { text: t('retail.customers'), path: '/pos/customers', icon: <People />, permission: 'pos.sale.view' },
      { text: t('retail.backorderApprovals', 'Backorder Approvals'), path: '/pos/backorders', icon: <AssignmentTurnedIn />, permission: 'pos.sale.manage' }
    ]
  },

  // ========== REPORTING & ANALYTICS ==========
  {
    text: t('nav.reportingAnalytics'),
    icon: <Assessment />,
    group: 'analytics',
    order: 12,
    permission: 'finance.reports.view',
    children: [
      { text: t('reports.financial'), path: '/reports', icon: <Assessment />, permission: 'finance.reports.view' },
      { text: t('reports.inventory'), path: '/reports/inventory', icon: <InventoryIcon />, permission: 'inventory.reports.view' },
      { text: t('reports.ai'), path: '/reports/ai-insights', icon: <Stars />, permission: 'finance.reports.view' },
      { text: t('reports.trade'), path: '/reports/trade', icon: <LocalShipping />, permission: 'trade.shipment.view' },
      { text: t('reports.snapshots'), path: '/reports/snapshots', icon: <HistoryIcon />, permission: 'finance.reports.view' }
    ]
  },

  // ========== COMPLIANCE & AUDIT ==========
  {
    text: t('nav.complianceAudit'),
    icon: <Gavel />,
    group: 'compliance',
    order: 13,
    permission: 'admin.audit.view',
    children: [
      { text: t('compliance.tax'), path: '/compliance/tax', icon: <LocalAtm />, permission: 'admin.tax.view' },
      { text: t('compliance.hmrc'), path: '/compliance/hmrc-mtd', icon: <CloudUploadIcon />, permission: 'admin.settings.manage' },
      { text: t('compliance.statutory'), path: '/compliance/uk-statutory', icon: <Gavel />, permission: 'admin.settings.manage' },
      { text: t('compliance.governance'), path: '/compliance/governance', icon: <Security />, permission: 'admin.settings.manage' },
      { text: t('compliance.policies'), path: '/compliance/policies/expense', icon: <Policy />, permission: 'admin.settings.manage' },
      { text: t('compliance.approvalsWorkbench', 'Approvals Workbench'), path: '/compliance/approvals/workbench', icon: <Verified /> },
      { text: t('compliance.notificationsApprovals', 'Notifications & Approvals'), path: '/notifications-approvals', icon: <Notifications /> },
      { text: t('compliance.workflowOverrides'), path: '/compliance/approvals/overrides', icon: <Tune />, permission: 'admin.settings.manage' },
      { text: t('compliance.revenueRecognition', 'Revenue Recognition'), path: '/compliance/policies/revenue', icon: <TrendingUp />, permission: 'admin.settings.manage' },
      { divider: true },
      { text: t('compliance.auditLog'), path: '/compliance/audit-log', icon: <Assessment />, permission: 'admin.audit.view' },
      { text: t('compliance.auditInspector', 'Audit Inspector'), path: '/compliance/audit-inspector', icon: <Verified />, permission: 'admin.audit.view' },
      { text: t('compliance.activityLog'), path: '/compliance/activity-logs', icon: <HistoryIcon />, permission: 'admin.audit.view' }
    ]
  },

  // ========== ADMINISTRATION ==========
  {
    text: t('nav.administration'),
    icon: <SettingsIcon />,
    group: 'admin',
    order: 14,
    permission: 'admin.user.view',
    children: [
      { text: t('admin.users'), path: '/admin/users', icon: <People />, permission: 'admin.user.view' },
      { text: t('admin.roles'), path: '/admin/roles', icon: <Security />, permission: 'admin.user.view' },
      { text: t('admin.companies'), path: '/admin/companies', icon: <Business />, permission: 'admin.settings.manage' },
      { text: t('admin.currencies'), path: '/admin/currencies', icon: <CurrencyExchange />, permission: 'admin.settings.manage' },
      { text: t('admin.businessSettings', 'Business Settings'), path: '/admin/business-settings', icon: <Business />, permission: 'admin.settings.manage' },
      { text: t('admin.securitySettings', 'Security Settings'), path: '/admin/security', icon: <Security />, permission: 'admin.settings.manage' },
      { text: t('admin.api', 'API Access'), path: '/admin/api-integrations', icon: <Api />, permission: 'admin.settings.manage' },
      { text: t('admin.posHardware', 'POS Hardware'), path: '/admin/pos-hardware', icon: <PointOfSale />, permission: 'pos.hardware.manage' },
      { divider: true },
      { text: t('admin.import'), path: '/admin/data-import', icon: <CloudUploadIcon />, permission: 'admin.settings.manage' },
      { text: t('admin.export', 'Data Export Center'), path: '/admin/data-export', icon: <Description />, permission: 'admin.settings.manage' },
      { text: t('admin.documentVerification'), path: '/admin/document-verification', icon: <Verified />, permission: 'admin.settings.manage' },
      { text: t('admin.documentPlatform', 'Document Templates'), path: '/document-platform', icon: <ArticleIcon />, permission: 'admin.settings.manage' },
      { divider: true },
      { text: t('admin.notifications'), path: '/admin/notifications', icon: <Notifications />, permission: 'admin.settings.manage' },
      { text: t('admin.subscription'), path: '/admin/subscription', icon: <LocalAtm />, permission: 'admin.settings.manage' },
      { text: t('admin.manual'), path: '__open_manual__', icon: <MenuBookIcon /> }
    ]
  },

  // ========== PLATFORM ADMIN (Super Admin Only) ==========
  {
    text: t('nav.platformAdmin'),
    icon: <SupervisorAccount />,
    group: 'platform',
    order: 15,
    permission: 'admin.superadmin.view',
    children: [
      { text: t('platform.dashboard'), path: '/superadmin', icon: <Dashboard />, permission: 'admin.superadmin.view' },
      { text: t('platform.subscriptionPlans'), path: '/admin/subscriptions/plans', icon: <LocalAtm />, permission: 'admin.superadmin.view' },
      { text: t('platform.tenants'), path: '/admin/subscriptions/customers', icon: <CreditCard />, permission: 'admin.superadmin.view' },
      { text: t('platform.revenue'), path: '/admin/revenue', icon: <TrendingUp />, permission: 'admin.superadmin.view' },
      { divider: true },
      { text: t('platform.leads', 'Registration Leads'), path: '/admin/leads', icon: <PersonSearch />, permission: 'admin.superadmin.view' },
      { text: t('platform.demoRequests', 'Demo Requests'), path: '/admin/demo-requests', icon: <ContactPhone />, permission: 'admin.superadmin.view' },
      { text: t('platform.pricing', 'Pricing & Plans'), path: '/admin/pricing', icon: <LocalAtm />, permission: 'admin.superadmin.view' },
      { text: t('platform.coupons'), path: '/admin/coupons', icon: <Stars />, permission: 'admin.superadmin.view' },
      { text: t('platform.blogs'), path: '/admin/blog', icon: <Description />, permission: 'admin.superadmin.view' },
      { text: t('platform.blogCategories'), path: '/admin/blog?tab=1', icon: <Category />, permission: 'admin.superadmin.view' },
      { divider: true },
      { text: t('platform.apiAccess'), path: '/admin/api-access', icon: <Api />, permission: 'admin.superadmin.view' },
      { text: t('platform.verify'), path: '/verify', icon: <QrCode />, permission: 'admin.superadmin.view' },
      { divider: true },
      {
        text: t('platform.partners'),
        icon: <Handshake />,
        permission: 'admin.superadmin.view',
        children: [
          { text: t('platform.partnerList'), path: '/admin/partners', icon: <People />, permission: 'admin.superadmin.view' },
          { text: t('platform.withdrawals'), path: '/admin/partners/withdrawals', icon: <AccountBalanceWallet />, permission: 'admin.superadmin.view' },
          { text: t('platform.partnerDeals'), path: '/admin/partners/deals', icon: <Handshake />, permission: 'admin.superadmin.view' },
          { text: t('platform.partnerHelpdesk'), path: '/admin/partners/helpdesk', icon: <SupportAgent />, permission: 'admin.superadmin.view' },
          { text: t('platform.partnerSla'), path: '/admin/partners/sla', icon: <Gavel />, permission: 'admin.superadmin.view' }
        ]
      }
    ]
  }
];

const SubscriptionBanner = () => {
  const { subscriptionStatus } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  if (!subscriptionStatus) return null;

  const { status, daysRemaining } = subscriptionStatus;
  const isTrial = status === 'trial';

  // Only show banner for trials ending soon (<= 7 days) or active subscriptions ending soon
  if (daysRemaining == null || daysRemaining > 7) return null;

  return (
    <Box
      sx={{
        bgcolor: isTrial ? 'warning.main' : 'error.main',
        color: isTrial ? 'warning.contrastText' : 'error.contrastText',
        py: 1,
        px: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        borderRadius: 1,
        mb: 2,
        boxShadow: theme.shadows[2],
        animation: 'fadeInDown 0.5s ease-out'
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {isTrial
          ? `Your trial period ends in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}. Upgrade now to ensure uninterrupted service.`
          : `Your subscription expires in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}. Please renew to keep your account active.`
        }
      </Typography>
      <Button
        variant="contained"
        size="small"
        onClick={() => navigate('/pricing')}
        sx={{
          bgcolor: 'white',
          color: isTrial ? 'warning.main' : 'error.main',
          fontWeight: 700,
          '&:hover': {
            bgcolor: isTrial ? 'warning.contrastText' : 'error.contrastText',
          }
        }}
      >
        Upgrade Now
      </Button>
    </Box>
  );
};

const TrialExpiryModal = () => {
  const { subscriptionStatus } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  React.useEffect(() => {
    if (subscriptionStatus?.status === 'trial' &&
      subscriptionStatus?.daysRemaining != null &&
      subscriptionStatus?.daysRemaining <= 3 &&
      !sessionStorage.getItem('trial_expiry_modal_shown')) {
      setOpen(true);
      sessionStorage.setItem('trial_expiry_modal_shown', 'true');
    }
  }, [subscriptionStatus]);

  if (!subscriptionStatus) return null;

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ fontWeight: 800, textAlign: 'center', pt: 3, pb: 1 }}>
        Trial Expiring Soon
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Your FluxERP trial will expire in <strong>{subscriptionStatus.daysRemaining} day{subscriptionStatus.daysRemaining === 1 ? '' : 's'}</strong>.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upgrade now to avoid losing access to your dashboard and business data.
        </Typography>
        <Alert severity="info" sx={{ mt: 3, textAlign: 'left', borderRadius: 2 }}>
          All your data will be preserved when you upgrade to a paid plan.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 4, px: 3, gap: 2 }}>
        <Button onClick={() => setOpen(false)} color="inherit" sx={{ fontWeight: 600 }}>
          Remind Me Later
        </Button>
        <Button
          variant="contained"
          onClick={() => { setOpen(false); navigate('/pricing'); }}
          sx={{
            bgcolor: 'secondary.main',
            color: 'secondary.contrastText',
            fontWeight: 700,
            px: 3,
            '&:hover': { bgcolor: 'secondary.dark' }
          }}
        >
          Upgrade Now
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [quickAddAnchorEl, setQuickAddAnchorEl] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [navSearch, setNavSearch] = useState('');
  const [modalStates, setModalStates] = useState({
    customer: false,
    vendor: false,
    item: false,
    opportunity: false,
    leave: false
  });

  const { mode } = useThemeMode();
  const { user, logout, hasPermission, subscriptionStatus, allowedModules } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const planName = subscriptionStatus?.plan?.toLowerCase() || subscriptionStatus?.planName?.toLowerCase() || '';
  const isSoloPlan = planName === 'solo';

  const toggleModal = (modal, state) => {
    setModalStates(prev => ({ ...prev, [modal]: state }));
    setQuickAddAnchorEl(null);
  };

  const toggleSection = (text) => {
    setExpandedSections(prev => ({ ...prev, [text]: !prev[text] }));
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleQuickAddOpen = (event) => setQuickAddAnchorEl(event.currentTarget);
  const handleQuickAddClose = () => setQuickAddAnchorEl(null);

  const handleLogout = () => {
    logout();
    enqueueSnackbar('Logged out successfully', { variant: 'info' });
    navigate('/login');
    handleMenuClose();
  };

  // Fetch active company country for UK-only item filtering
  const { data: currentCompanyData } = useQuery(
    ['layout-current-company'],
    async () => {
      const res = await companiesAPI.getCurrent();
      return res.data?.company || res.data?.data || res.data;
    },
    { staleTime: 5 * 60 * 1000, retry: 1 }
  );
  const companyCountry = currentCompanyData?.country;

  // Filter items based on RBAC permissions, RBSC company scope, allowedModules, and UK/plan restrictions
  const filteredMenuItems = useMemo(() => {
    const activeAllowedModules = currentCompanyData?.allowedModules || user?.company?.allowedModules || allowedModules;

    const filterMenuInternal = (items, depth = 0) => {
      const result = [];
      for (const item of items) {
        // RBSC: Hide plan-restricted groups for Solo plan
        if (item.group && PLAN_RESTRICTED_GROUPS.has(item.group) && isSoloPlan) continue;
        
        // RBSC: Hide module groups if they are restricted by company allowedModules
        if (item.group && item.group !== 'core' && item.group !== 'platform' && activeAllowedModules && Array.isArray(activeAllowedModules)) {
          const isAllowed = activeAllowedModules.includes(item.group) ||
            (item.group === 'eam' && (activeAllowedModules.includes('finance') || activeAllowedModules.includes('inventory'))) ||
            (item.group === 'trade' && (activeAllowedModules.includes('procurement') || activeAllowedModules.includes('inventory')));
          if (!isAllowed) continue;
        }

        // RBSC: Hide UK-only paths for non-UK companies
        if (item.path && UK_ONLY_PATHS.has(item.path) && (!companyCountry || !isUKCompany(companyCountry))) continue;

        if (item.children) {
          // Platform super-admin section strictly requires superadmin permission
          if (item.permission === 'admin.superadmin.view' && !hasPermission('admin.superadmin.view')) continue;

          // Recursively filter children based on RBAC
          const filteredChildren = filterMenuInternal(item.children, depth + 1);
          // Hide parent section if it has no non-divider children left
          const hasVisibleChildren = filteredChildren.some(c => !c.divider);
          if (!hasVisibleChildren) continue;

          result.push({ ...item, isSubItem: depth > 0, children: filteredChildren });
        } else {
          // RBAC: Leaf item check (with backward-compatible alias fallback for newly introduced permissions)
          if (item.permission) {
            let allowed = hasPermission(item.permission);
            if (!allowed && item.permission === 'finance.lease.view') {
              allowed = hasPermission('finance.journal_entry.view');
            }
            if (!allowed) continue;
          }
          result.push({ ...item, isSubItem: depth > 0 });
        }
      }
      return result;
    };

    // Get menu items and sort by order property to ensure correct sequence
    const menuItems = getMenuItems(t);
    const sortedMenuItems = [...menuItems].sort((a, b) => (a.order || 999) - (b.order || 999));

    const filtered = filterMenuInternal(sortedMenuItems);

    return filtered;
  }, [t, hasPermission, companyCountry, isSoloPlan, allowedModules, currentCompanyData, user]);

  // Search-filtered nav items: applies the user's search query on top of permission-filtered items.
  // Returns items matching by parent name or child name; matching parents expose only their
  // matching children. When no query is active, returns the full permission-filtered list.
  const searchFilteredItems = useMemo(() => {
    const query = navSearch.trim().toLowerCase();
    if (!query) return filteredMenuItems;

    const result = [];
    for (const item of filteredMenuItems) {
      if (item.children) {
        // Check if parent name itself matches
        const parentMatches = item.text.toLowerCase().includes(query);
        // Filter children that match
        const matchingChildren = item.children.filter(
          child => !child.divider && child.text && child.text.toLowerCase().includes(query)
        );
        if (parentMatches && matchingChildren.length === 0) {
          // Parent name matches but no specific child matches — show all children
          result.push(item);
        } else if (matchingChildren.length > 0) {
          // Show parent with only the matching children
          result.push({ ...item, children: matchingChildren });
        }
        // If neither parent nor any child matches, skip entirely
      } else {
        // Leaf item
        if (!item.divider && item.text && item.text.toLowerCase().includes(query)) {
          result.push(item);
        }
      }
    }
    return result;
  }, [filteredMenuItems, navSearch]);

  // Initialize expanded sections for all parent items whenever filtered menu items change
  useEffect(() => {
    const collectParentItems = (items) => {
      const state = {};
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          state[item.text] = false;
          const childState = collectParentItems(item.children);
          Object.assign(state, childState);
        }
      });
      return state;
    };
    const initialExpandedState = collectParentItems(filteredMenuItems);
    setExpandedSections(prev => {
      if (Object.keys(prev).length === 0) {
        return initialExpandedState;
      }
      return prev;
    });
  }, [filteredMenuItems]);

  // When a search query is active, auto-expand all parents so matches are visible.
  // When cleared, collapse everything back to the default closed state.
  useEffect(() => {
    const query = navSearch.trim().toLowerCase();
    if (!query) return;
    // Expand every parent in the search-filtered result set
    const expandAll = (items) => {
      const state = {};
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          state[item.text] = true;
          Object.assign(state, expandAll(item.children));
        }
      });
      return state;
    };
    setExpandedSections(prev => ({ ...prev, ...expandAll(searchFilteredItems) }));
  }, [navSearch, searchFilteredItems]);

  const breadcrumbs = useMemo(() => {
    for (const item of filteredMenuItems) {
      if (item.path && item.path !== '__open_manual__' && item.path === location.pathname) {
        return [{ text: item.text, path: item.path }];
      }
      if (item.children) {
        const child = item.children.find(c => c.path !== '__open_manual__' && c.path === location.pathname);
        if (child) return [{ text: item.text }, { text: child.text, path: child.path }];
      }
    }
    return [{ text: 'FluxERP' }];
  }, [filteredMenuItems, location.pathname]);

  const SIDEBAR = {
    bg: '#163300',
    text: '#FFFFFF',
    textSecondary: 'rgba(255, 255, 255, 0.65)',
    activeItemBg: '#9FE870',
    activeItemText: '#163300',
    hoverItemBg: 'rgba(159, 232, 112, 0.12)',
    divider: 'rgba(255, 255, 255, 0.12)',
    icon: 'rgba(255, 255, 255, 0.7)',
    activeIcon: '#163300',
    sectionLabel: 'rgba(255, 255, 255, 0.45)',
  };

  const renderMenuItems = (items) =>
    (Array.isArray(items) ? items : []).map(item => (
      <React.Fragment key={item.text}>
        {item.children && item.children.length > 0 ? (
          // Section with children - expandable/collapsible
          <>
            <ListItem disablePadding sx={{ mt: 0.75 }}>
              <ListItemButton
                onClick={() => toggleSection(item.text)}
                sx={{
                  py: 0.75,
                  px: 2,
                  borderRadius: '6px',
                  mx: 1,
                  '&:hover': {
                    bgcolor: SIDEBAR.hoverItemBg,
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 34, color: SIDEBAR.icon }}>
                  <Box sx={{ fontSize: '1.2rem', display: 'flex' }}>{item.icon}</Box>
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: SIDEBAR.text,
                    letterSpacing: '0.01em',
                  }}
                />
                <Box sx={{ color: SIDEBAR.textSecondary, display: 'flex', alignItems: 'center' }}>
                  {expandedSections[item.text] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </Box>
              </ListItemButton>
            </ListItem>
            <Collapse in={expandedSections[item.text] === true} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderMenuItems(item.children)}
              </List>
            </Collapse>
          </>
        ) : (
          // Regular menu item (leaf node) - skip rendering if it's a divider
          !item.divider && (
            <ListItem disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                selected={item.path !== '__open_manual__' && location.pathname === item.path}
                onClick={() => {
                  if (item.path === '__open_manual__') {
                    const docsUrl = `${(process.env.REACT_APP_API_BASE_URL || '').replace('/api', '')}/app/user-manual/overview`;
                    window.open(docsUrl, '_blank', 'noopener,noreferrer');
                    if (isMobile) setMobileOpen(false);
                  } else if (item.path) {
                    navigate(item.path);
                    if (isMobile) setMobileOpen(false);
                  }
                }}
                sx={{
                  paddingInlineStart: item.isSubItem ? '36px' : '16px',
                  paddingInlineEnd: '16px',
                  py: 0.75,
                  mx: 1,
                  borderRadius: '6px',
                  transition: 'all 0.18s ease',
                  '&:hover': {
                    bgcolor: SIDEBAR.hoverItemBg,
                    '& .MuiListItemText-primary': { color: '#FFFFFF' },
                  },
                  '&.Mui-selected': {
                    bgcolor: SIDEBAR.activeItemBg,
                    '& .MuiListItemText-primary': { color: SIDEBAR.activeItemText },
                    '& .MuiListItemIcon-root': { color: SIDEBAR.activeIcon },
                    '&:hover': {
                      bgcolor: '#8FD760',
                    }
                  }
                }}
              >
                <ListItemIcon sx={{
                  minWidth: 34,
                  color: (item.path !== '__open_manual__' && location.pathname === item.path) ? SIDEBAR.activeIcon : SIDEBAR.icon
                }}>
                  <Box sx={{ fontSize: '1.2rem', display: 'flex' }}>{item.icon}</Box>
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.8125rem',
                    fontWeight: (item.path !== '__open_manual__' && location.pathname === item.path) ? 600 : 400,
                    color: (item.path !== '__open_manual__' && location.pathname === item.path) ? SIDEBAR.activeItemText : SIDEBAR.textSecondary,
                  }}
                />
                {item.path === '__open_manual__' && (
                  <OpenInNewIcon sx={{ fontSize: '.75rem', color: SIDEBAR.textSecondary, ml: .5, flexShrink: 0 }} />
                )}
              </ListItemButton>
            </ListItem>
          )
        )}
      </React.Fragment>
    ));

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#163300' }}>
      {/* Sidebar Logo Header */}
      <Box sx={{
        px: 2,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        margin: '0 auto',
        gap: 1.5,
        minHeight: 64,
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        <Box sx={{
          height: 50,
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          overflow: 'hidden',
          '& img': {
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }
        }}>
          <img
            src={`/assets/logo-${mode === 'light' ? 'dark' : 'dark'}.png`}
            alt="FluxERP"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>
        {isMobile && (
          <IconButton
            onClick={handleDrawerToggle}
            size="small"
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}
          >
            {theme.direction === 'rtl' ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        )}
      </Box>

      {/* Navigation Search */}
      <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder={t('common.searchMenu', 'Search menu…')}
          value={navSearch}
          onChange={e => setNavSearch(e.target.value)}
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.45)' }} />
              </InputAdornment>
            ),
            endAdornment: navSearch ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setNavSearch('')}
                  edge="end"
                  aria-label="Clear search"
                  sx={{ color: 'rgba(255,255,255,0.5)', p: 0.25, '&:hover': { color: '#fff' } }}
                >
                  <ClearIcon sx={{ fontSize: '0.875rem' }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              bgcolor: 'rgba(255,255,255,0.07)',
              fontSize: '0.8rem',
              color: '#fff',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
              '&:hover fieldset': { borderColor: 'rgba(159,232,112,0.4)' },
              '&.Mui-focused fieldset': { borderColor: 'rgba(159,232,112,0.7)', borderWidth: 1 },
            },
            '& .MuiOutlinedInput-input': {
              py: 0.75,
              '&::placeholder': { color: 'rgba(255,255,255,0.4)', opacity: 1 },
            },
          }}
        />
      </Box>

      {/* Sidebar Nav Items */}
      <Box sx={{
        flexGrow: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        py: 1.5,
        '&::-webkit-scrollbar': {
          width: 6,
          display: 'block'
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'rgba(159, 232, 112, 0.45)',
          borderRadius: 3,
          '&:hover': {
            bgcolor: 'rgba(159, 232, 112, 0.7)',
          }
        },
        '&::-webkit-scrollbar-track': {
          bgcolor: 'rgba(255, 255, 255, 0.03)',
        }
      }}>
        <List sx={{ px: 0 }}>
          {searchFilteredItems.length === 0 && navSearch.trim() ? (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <SearchIcon sx={{ fontSize: '1.5rem', color: 'rgba(255,255,255,0.2)', mb: 0.5 }} />
              <Typography sx={{ fontSize: '0.775rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
                {t('dashboard.noResultsFor', 'No results for')} &ldquo;{navSearch}&rdquo;
              </Typography>
            </Box>
          ) : (
            renderMenuItems(searchFilteredItems)
          )}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <AppBar
        position="fixed"
        elevation={0}
        className="LayoutAppBar"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          left: theme.direction === 'rtl' ? 0 : { xs: 0, md: `${drawerWidth}px` },
          right: theme.direction === 'rtl' ? { xs: 0, md: `${drawerWidth}px` } : 0,
          ml: 0,
          mr: 0,
          bgcolor: theme.palette.mode === 'light' ? '#FFFFFF' : '#1C1F1A',
          color: theme.palette.mode === 'light' ? '#0E0F0C' : '#FFFFFF',
          borderBottom: '1px solid',
          borderColor: theme.palette.mode === 'light' ? 'rgba(14, 15, 12, 0.12)' : 'rgba(255, 255, 255, 0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: theme.direction === 'rtl' ? 0 : 2,
              ml: theme.direction === 'rtl' ? 2 : 0,
              display: { md: 'none' }
            }}
          >
            <MenuIcon />
          </IconButton>
          <Breadcrumbs
            separator={theme.direction === 'rtl' ? <NavigateBefore fontSize="small" /> : <NavigateNext fontSize="small" />}
            sx={{ flexGrow: 1, '& .MuiBreadcrumbs-separator': { mx: 0.5 } }}
          >
            {breadcrumbs.map((crumb, idx) => {
              const isLast = idx === breadcrumbs.length - 1;
              return isLast ? (
                <Typography
                  key={idx}
                  variant="h6"
                  noWrap
                  sx={{ fontWeight: 600, color: theme.palette.mode === 'dark' ? 'primary.main' : '#163300', fontSize: '1rem' }}
                >
                  {crumb.text}
                </Typography>
              ) : (
                <MuiLink
                  key={idx}
                  component="button"
                  onClick={() => crumb.path && navigate(crumb.path)}
                  underline="hover"
                  sx={{ fontWeight: 500, fontSize: '0.875rem', color: 'text.secondary', cursor: crumb.path ? 'pointer' : 'default', background: 'none', border: 'none', p: 0 }}
                >
                  {crumb.text}
                </MuiLink>
              );
            })}
          </Breadcrumbs>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Quick Add Button */}
            <Tooltip title={t('dashboard.quickActions', 'Quick Actions')}>
              <IconButton
                onClick={handleQuickAddOpen}
                sx={{
                  bgcolor: theme.palette.mode === 'light' ? 'rgba(159, 232, 112, 0.1)' : 'rgba(159, 232, 112, 0.05)',
                  color: '#163300',
                  '&:hover': { bgcolor: 'rgba(159, 232, 112, 0.2)' }
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={quickAddAnchorEl}
              open={Boolean(quickAddAnchorEl)}
              onClose={handleQuickAddClose}
              transformOrigin={{ horizontal: theme.direction === 'rtl' ? 'left' : 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: theme.direction === 'rtl' ? 'left' : 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={() => toggleModal('customer', true)}>
                <ListItemIcon sx={{ minWidth: 36, mr: theme.direction === 'rtl' ? 0 : 1, ml: theme.direction === 'rtl' ? 1 : 0 }}><Business fontSize="small" /></ListItemIcon>
                {t('customers.addCustomer', 'Add Customer')}
              </MenuItem>
              <MenuItem onClick={() => toggleModal('vendor', true)}>
                <ListItemIcon sx={{ minWidth: 36, mr: theme.direction === 'rtl' ? 0 : 1, ml: theme.direction === 'rtl' ? 1 : 0 }}><Group fontSize="small" /></ListItemIcon>
                {t('vendors.addVendor', 'Add Vendor')}
              </MenuItem>
              <MenuItem onClick={() => toggleModal('item', true)}>
                <ListItemIcon sx={{ minWidth: 36, mr: theme.direction === 'rtl' ? 0 : 1, ml: theme.direction === 'rtl' ? 1 : 0 }}><Category fontSize="small" /></ListItemIcon>
                {t('inventory.addItem', 'Add Inventory Item')}
              </MenuItem>
              <MenuItem onClick={() => toggleModal('opportunity', true)}>
                <ListItemIcon sx={{ minWidth: 36, mr: theme.direction === 'rtl' ? 0 : 1, ml: theme.direction === 'rtl' ? 1 : 0 }}><TrendingUp fontSize="small" /></ListItemIcon>
                {t('crm.newOpportunity', 'New Opportunity')}
              </MenuItem>
              <MenuItem onClick={() => toggleModal('leave', true)}>
                <ListItemIcon sx={{ minWidth: 36, mr: theme.direction === 'rtl' ? 0 : 1, ml: theme.direction === 'rtl' ? 1 : 0 }}><LocalAtm fontSize="small" /></ListItemIcon>
                {t('hrm.applyLeave', 'Apply for Leave')}
              </MenuItem>
            </Menu>

            <CompanySwitcher />
            <LanguageSelector />
            <AlertNotificationCenter />
            <Typography variant="body2" sx={{
              mr: theme.direction === 'rtl' ? 0 : 1,
              ml: theme.direction === 'rtl' ? 1 : 0,
              display: { xs: 'none', sm: 'block' },
              color: 'text.secondary',
              fontSize: '0.875rem'
            }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <IconButton
              size="large"
              onClick={handleMenuOpen}
              sx={{ color: theme.palette.mode === 'dark' ? 'primary.main' : '#163300' }}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: theme.direction === 'rtl' ? 'left' : 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: theme.direction === 'rtl' ? 'left' : 'right' }}
            >
              <MenuItem onClick={() => { navigate('/admin/settings'); handleMenuClose(); }}>
                <ListItemIcon sx={{ minWidth: 36, mr: theme.direction === 'rtl' ? 0 : 1, ml: theme.direction === 'rtl' ? 1 : 0 }}><SettingsIcon fontSize="small" /></ListItemIcon>
                {t('common.settings', 'Settings')}
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon sx={{ minWidth: 36, mr: theme.direction === 'rtl' ? 0 : 1, ml: theme.direction === 'rtl' ? 1 : 0 }}><Logout fontSize="small" /></ListItemIcon>
                {t('nav.logout', 'Logout')}
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Global Modals */}
      <QuickAddCustomer open={modalStates.customer} onClose={() => toggleModal('customer', false)} onSuccess={() => { }} />
      <QuickAddVendor open={modalStates.vendor} onClose={() => toggleModal('vendor', false)} onSuccess={() => { }} />
      <QuickAddInventoryItem open={modalStates.item} onClose={() => toggleModal('item', false)} onSuccess={() => { }} />
      <QuickAddOpportunity open={modalStates.opportunity} onClose={() => toggleModal('opportunity', false)} onSuccess={() => { }} />
      <QuickAddLeaveRequest open={modalStates.leave} onClose={() => toggleModal('leave', false)} onSuccess={() => { }} />

      <Box component="nav" sx={{ width: { xs: 0, md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          anchor={theme.direction === 'rtl' ? 'right' : 'left'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: theme.direction === 'rtl' ? 'none' : undefined,
              borderLeft: theme.direction === 'rtl' ? '1px solid rgba(255, 255, 255, 0.1)' : undefined,
              boxShadow: theme.shadows[8]
            }
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          anchor={theme.direction === 'rtl' ? 'right' : 'left'}
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: theme.direction === 'rtl' ? 'none' : undefined,
              borderLeft: theme.direction === 'rtl' ? '1px solid rgba(255, 255, 255, 0.1)' : undefined,
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` }, minWidth: 0, maxWidth: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` }, minHeight: '100vh', backgroundColor: 'background.default', overflowX: 'hidden' }}>
        <Toolbar />
        <SubscriptionBanner />
        <ErrorBoundary>
          <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><LoadingSpinner /></Box>}>
            <Outlet />
          </React.Suspense>
        </ErrorBoundary>
        <TrialExpiryModal />
      </Box>
    </Box>
  );
};

export default Layout;
